import React, { useState, useEffect } from 'react';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Type declaration for window.MSStream
declare global {
  interface Window {
    MSStream?: any;
  }
}

interface CheckoutFormProps {
  formData: {
    fullName: string;
    email: string;
    idNumber: string;
    address: string;
    phone: string;
    donationAmount: string;
    termsAccepted: boolean;
    language: string;
  };
  onGoBack: () => void;
}

// Translations for the checkout form
const translations = {
  ca: {
    back: "Tornar a la informació",
    summary: "Resum de la donació",
    amount: "Import:",
    donorInfo: "Informació del donant:",
    paymentDetails: "Detalls de pagament",
    completePayment: "Completar pagament",
    processing: "Processant...",
    paymentComplete: "Pagament completat!",
    paymentProcessing: "El seu pagament s'està processant.",
    paymentFailed: "El seu pagament no s'ha pogut completar, si us plau, torneu-ho a provar.",
    somethingWrong: "Alguna cosa ha anat malament.",
    error: "Hi ha hagut un error",
    unexpectedError: "Hi ha hagut un error inesperat"
  },
  es: {
    back: "Volver a la información",
    summary: "Resumen de la donación",
    amount: "Importe:",
    donorInfo: "Información del donante:",
    paymentDetails: "Detalles de pago",
    completePayment: "Completar pago",
    processing: "Procesando...",
    paymentComplete: "¡Pago completado!",
    paymentProcessing: "Su pago se está procesando.",
    paymentFailed: "Su pago no se ha podido completar, por favor, inténtelo de nuevo.",
    somethingWrong: "Algo ha ido mal.",
    error: "Ha habido un error",
    unexpectedError: "Ha habido un error inesperado"
  },
  en: {
    back: "Back to information",
    summary: "Donation summary",
    amount: "Amount:",
    donorInfo: "Donor information:",
    paymentDetails: "Payment details",
    completePayment: "Complete payment",
    processing: "Processing...",
    paymentComplete: "Payment completed!",
    paymentProcessing: "Your payment is being processed.",
    paymentFailed: "Your payment could not be completed, please try again.",
    somethingWrong: "Something went wrong.",
    error: "There was an error",
    unexpectedError: "There was an unexpected error"
  }
};

const CheckoutForm: React.FC<CheckoutFormProps> = ({ formData, onGoBack }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Get current language translations
  const currentLang = formData.language || 'ca';
  const t = translations[currentLang as keyof typeof translations];
  
  // Function to get the appropriate redirect URL
  const getRedirectUrl = (path: string): string => {
    // Get the language prefix
    let langPrefix = '';
    if (formData.language === 'es') {
      langPrefix = '/es';
    } else if (formData.language === 'en') {
      langPrefix = '/en';
    }
  
    // Usa siempre el origen actual (window.location.origin) + prefijo + path
    return `${window.location.origin}${langPrefix}${path}`;
  };
  
  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret'
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      if (!paymentIntent) return;
      
      switch (paymentIntent.status) {
        case 'succeeded':
          setMessage(t.paymentComplete);
          break;
        case 'processing':
          setMessage(t.paymentProcessing);
          break;
        case 'requires_payment_method':
          setMessage(t.paymentFailed);
          break;
        default:
          setMessage(t.somethingWrong);
          break;
      }
    });
  }, [stripe, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Success redirect URL using the appropriate path
        return_url: getRedirectUrl('/success.html'),
        receipt_email: formData.email,
        payment_method_data: {
          billing_details: {
            name: formData.fullName,
            email: formData.email
          }
        }
      }
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        // Show validation errors in the form
        setMessage(error.message || t.error);
      } else {
        // Only redirect to failure page for unexpected errors or actual payment failures
        setMessage(t.unexpectedError);
        window.location.href = getRedirectUrl('/failure.html');
      }
    }

    setIsLoading(false);
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <button 
        type="button" 
        className="back-button" 
        onClick={onGoBack}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>
        {t.back}
      </button>
      
      <div className="form-section enhanced-form-section">
        <div className="payment-summary">
          <h3 className="section-title enhanced-section-title">
            <div className="section-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path><path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path><path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path></svg>
            </div>
            {t.summary}
          </h3>
          <div className="summary-details">
            <div className="summary-row">
              <span className="summary-label">{t.amount}</span>
              <span className="summary-value">{formData.donationAmount}€</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">{t.donorInfo}</span>
              <span className="summary-value">{formData.fullName}</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-section enhanced-form-section">
        <h3 className="section-title enhanced-section-title">
          <div className="section-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
          </div>
          {t.paymentDetails}
        </h3>
        <PaymentElement id="payment-element" />
      </div>
      
      <button 
        disabled={isLoading || !stripe || !elements} 
        id="submit" 
        className="payment-button"
      >
        <span id="button-text">
          {isLoading ? (
            <div className="spinner-container">
              <div className="spinner"></div>
              <span>{t.processing}</span>
            </div>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
              {t.completePayment}
            </>
          )}
        </span>
      </button>
      
      {/* Show any error or success messages */}
      {message && (
        <div id="payment-message" className={message.includes('error') ? 'error' : ''}>
          {message}
        </div>
      )}
    </form>
  );
};

export default CheckoutForm; 
